import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  Text,
} from "@chakra-ui/react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  AwardCriterionType,
  AwardSubCriterionAnswerFormat,
  type AwardSubCriterionDto,
  WeightType,
} from "../../../autogen/bff-api";
import { WeightInput } from "../../../pages/sourcing-events/buyer/wizard/components/WeightInput";
import { AWARD_SUB_CRITERION_ANSWER_FORMAT_OPTIONS } from "../../../pages/sourcing-events/buyer/wizard/utils/publicProcurementOptions";
import { TipTap } from "../../input/TipTap/TipTap";
import { FormOptionRow, FormSectionBand } from "./FormSectionBand";

// t('Sub-criterion name') t('E.g. Offered key personnel') t('A name is required')
// t('Weight') t('A weight is required') t('Must be between 1 and 100')
// t('Description — what is assessed?') t('Describe what is evaluated and how')
// t('Belongs under') t('Choose a criterion type') t('A criterion type is required')
// t('A suggestion — the weights are set per procurement, where they must total 100 % in the criterion')
// t('Answer format — how should the supplier answer?')
// t('Requires documentation') t('The supplier must upload an attachment')

/**
 * The criterion types a requirement can live under: the ones a procurement actually weights. The § 5c
 * exemption markers are excluded — they carry no weight and no card, so nothing hangs under them.
 * Mirrors the BFF's LibraryContent.SUB_CRITERION_HOST_TYPES, which rejects anything else.
 */
export const SUB_CRITERION_HOST_TYPES: AwardCriterionType[] = [
  AwardCriterionType.Price,
  AwardCriterionType.Cost,
  AwardCriterionType.Quality,
  AwardCriterionType.QualityEnvironmentalCriteria,
];

export interface AwardSubCriterionFormValues {
  name: string;
  weight: string;
  description: string;
  criterionType?: AwardCriterionType | "";
  answerFormat: AwardSubCriterionAnswerFormat;
  requiresDocumentation: boolean;
}

/** What the option rows below the fields are allowed to read while the form is being filled in. */
export interface AwardSubCriterionFormState {
  /** The weight as it currently parses, so an option can name the share it is about to store. */
  weight?: number;
}

/** What the form yields: a requirement, plus where it belongs when it is written for the library. */
export type AwardSubCriterionSubmitValues = Omit<AwardSubCriterionDto, "id"> & {
  criterionType?: AwardCriterionType;
};

interface Props {
  /** Lets a button outside the form submit it, so the same fields work in a drawer and a modal. */
  formId: string;
  defaultValues?: AwardSubCriterionFormValues;
  /**
   * Present only when the requirement is written for the library, where nothing places it: it then asks
   * which kind of criterion it belongs under. Inside a procurement the criterion is the context, so
   * this is omitted.
   */
  criterionTypes?: AwardCriterionType[];
  /**
   * What the weight means here, which depends on whether the requirement has siblings to be a share of:
   *
   * - `hidden` — a requirement kept on its own. There is nothing to be a share of, and the standalone
   *   library DTO has no field for a weight at all.
   * - `optional` — inside a criterion in the library. The shares are a set that totals 100 within the
   *   criterion and travel with it, but a definition is allowed to be incomplete: a requirement picked
   *   from the library arrives unweighted, and opening it must not force a number to be invented.
   * - `required` — inside a criterion in a live procurement, where the weights decide the evaluation.
   */
  weight?: "hidden" | "optional" | "required";
  /**
   * Whether the supplier is asked anything on this requirement.
   *
   * - `asked` — the ordinary case: the buyer chooses an answer format and may demand documentation.
   * - `derived-from-price` — a requirement under a price criterion. Every such row is scored from the
   *   tendered price rather than answered, so there is no format to pick and no documentation to ask
   *   for: the supplier is shown no upload control for it, and the BFF rejects the demand outright.
   *   Offering the choices here would only lead the buyer into a validation error.
   */
  answer?: "asked" | "derived-from-price";
  onSubmit: (values: AwardSubCriterionSubmitValues) => Promise<void> | void;
  /**
   * Rendered inside the form, under the fields — where the "keep this for later" choices belong. Given
   * as a function when a choice needs to say something about what is currently typed; the weight is the
   * form's to know, and an option that stores it should be able to name it.
   */
  children?: React.ReactNode | ((state: AwardSubCriterionFormState) => React.ReactNode);
}

const emptyDefaults: AwardSubCriterionFormValues = {
  name: "",
  weight: "",
  description: "",
  criterionType: "",
  answerFormat: AwardSubCriterionAnswerFormat.FreeText,
  requiresDocumentation: false,
};

/** The one definition of what an "underkrav" asks for, shared by the wizard drawers and the library. */
export const AwardSubCriterionFields = ({
  formId,
  defaultValues,
  criterionTypes,
  weight: weightMode = "required",
  answer = "asked",
  onSubmit,
  children,
}: Props) => {
  const { t } = useTranslation();
  const asksTheSupplier = answer === "asked";
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<AwardSubCriterionFormValues>({ defaultValues: defaultValues ?? emptyDefaults });

  const isWeightHidden = weightMode === "hidden";
  const isWeightRequired = weightMode === "required";
  const typedWeight = useWatch({ control, name: "weight" });
  const parsedWeight = Math.round(Number.parseFloat((typedWeight ?? "").replace(",", ".")));
  const formState: AwardSubCriterionFormState = {
    weight: Number.isFinite(parsedWeight) ? parsedWeight : undefined,
  };

  const submitHandler = handleSubmit(async (values) => {
    // Integer-only, like the criterion weight: side-steps locale display, float drift and BigDecimal
    // precision mismatch with the BFF.
    const parsed = Math.round(Number.parseFloat(values.weight.replace(",", ".")));
    await onSubmit({
      name: values.name,
      description: values.description,
      weight: isWeightHidden || !Number.isFinite(parsed) ? undefined : { number: parsed, type: WeightType.Percentage },
      // A price row is scored, not answered: it keeps the harmless default rather than whatever the
      // form happens to hold, so reopening a criterion that changed type cannot smuggle one through.
      answerFormat: asksTheSupplier ? values.answerFormat : AwardSubCriterionAnswerFormat.FreeText,
      requiresDocumentation: asksTheSupplier ? values.requiresDocumentation : false,
      ...(values.criterionType ? { criterionType: values.criterionType } : {}),
    });
  });

  return (
    <form id={formId} data-testid="award-sub-criterion-form" onSubmit={submitHandler}>
      <Flex gap="4" mb="6" align="flex-start">
        <FormControl flex="1" isRequired isInvalid={errors.name !== undefined}>
          <FormLabel>{t("Sub-criterion name")}</FormLabel>
          <Input
            autoFocus
            autoComplete="off"
            data-1p-ignore
            placeholder={t("E.g. Offered key personnel") ?? ""}
            {...register("name", { required: t("A name is required") ?? "" })}
          />
          <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
        </FormControl>

        {/* The same control the criteria table and the criterion panel use, so a weight is stepped and
            typed the same way wherever it is edited. The form keeps holding a string; WeightInput hands
            back the rounded number and rejects everything that is not one. */}
        {!isWeightHidden && (
        <FormControl w="28" isRequired={isWeightRequired} isInvalid={errors.weight !== undefined}>
          <FormLabel>{t("Weight")}</FormLabel>
          <Controller
            name="weight"
            control={control}
            rules={{
              required: isWeightRequired ? (t("A weight is required") ?? "") : false,
              validate: (value) => {
                if (!isWeightRequired && value.trim() === "") return true;
                const parsed = Math.round(Number.parseFloat(value.replace(",", ".")));
                return (Number.isFinite(parsed) && parsed >= 1 && parsed <= 100) ||
                  (t("Must be between 1 and 100") ?? false);
              },
            }}
            render={({ field: { value, onChange } }) => (
              <WeightInput
                value={value === "" ? undefined : Number(value)}
                isDisabled={false}
                isInvalid={errors.weight !== undefined}
                ariaLabel={t("Weight")}
                onChange={(weight) => onChange(weight == null ? "" : String(weight))}
              />
            )}
          />
          <FormErrorMessage>{errors.weight?.message}</FormErrorMessage>
        </FormControl>
        )}
      </Flex>

      {criterionTypes && (
        <FormControl mb="6" isRequired isInvalid={errors.criterionType !== undefined}>
          <FormLabel>{t("Belongs under")}</FormLabel>
          <Select
            rounded="md"
            {...register("criterionType", { required: t("A criterion type is required") ?? "" })}
          >
            <option value="">{t("Choose a criterion type")}</option>
            {criterionTypes.map((type) => (
              <option key={type} value={type}>
                {t(type)}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.criterionType?.message}</FormErrorMessage>
          <FormHelperText>
            {t("A suggestion — the weights are set per procurement, where they must total 100 % in the criterion")}
          </FormHelperText>
        </FormControl>
      )}

      <FormControl mb="6" isRequired>
        <FormLabel>{t("Description — what is assessed?")}</FormLabel>
        <Controller
          name="description"
          control={control}
          render={({ field: { value, onChange } }) => (
            <TipTap
              content={value}
              onUpdate={onChange}
              editable={true}
              toolbar={{
                underline: false,
                headings: false,
                colors: false,
                link: false,
                table: false,
                image: false,
                template: false,
              }}
              debounceWait={0}
            />
          )}
        />
      </FormControl>

      {/* A dropdown rather than the criterion type's pills: this is one detail per requirement and a
          criterion can hold several, so four pills each would repeat down the page and compete with the
          type pills a level above. */}
      {asksTheSupplier && (
        <FormControl mb="2">
          <FormLabel>{t("Answer format — how should the supplier answer?")}</FormLabel>
          <Select rounded="md" {...register("answerFormat")}>
            {AWARD_SUB_CRITERION_ANSWER_FORMAT_OPTIONS.map((format) => (
              <option key={format} value={format}>
                {t(format)}
              </option>
            ))}
          </Select>
        </FormControl>
      )}
      {!asksTheSupplier && (
        <Text mb="2" fontSize="sm" color="smMuted">
          {t("This is scored from the tendered price, so the supplier answers nothing here.")}
        </Text>
      )}

      {/* Independent of the format: DOCUMENT means the answer itself is a file, this asks for evidence on
          top of whichever answer the format calls for. Withheld on a price row, which has no answer to
          attach evidence to and no upload control on the supplier's side to attach it with. */}
      {asksTheSupplier && (
        <Box mb={children ? "6" : "0"}>
          <Controller
            name="requiresDocumentation"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormOptionRow
                // Nothing follows it unless the caller adds a choice below, so the rule under the row
                // would otherwise hang against the bottom of the dialog.
                showDivider={!!children}
                isChecked={value}
                onChange={onChange}
                title={t("Requires documentation")}
                hint={t("The supplier must upload an attachment")}
              />
            )}
          />
        </Box>
      )}

      {/* The band only earns its place when there is a choice under it — the library modal passes none. */}
      {children && (
        <>
          <FormSectionBand>{t("Options")}</FormSectionBand>
          {typeof children === "function" ? children(formState) : children}
        </>
      )}
    </form>
  );
};
